import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useFlags } from '@/stores/identity';
import {
  BANNER_SEVERITY_THEMES,
  archiveBanner,
  getBannerStats,
  listAllBanners,
  publishBanner,
  type BannerStats,
} from '@/lib/banners';
import type { Banner, BannerStatus } from '@/lib/types';
import { humanize } from '@/lib/errors';
import { formatDate } from '@/lib/dates';

/**
 * /admin/banners — list, with publish / archive controls.
 */
export default function BannersList() {
  const flags = useFlags();
  const isAdmin = !!flags?.is_platform_admin;
  const [items, setItems] = useState<Banner[]>([]);
  const [stats, setStats] = useState<Record<string, BannerStats>>({});
  const [status, setStatus] = useState<'idle' | 'loading' | 'loaded' | 'error'>(
    'idle'
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function reload() {
    setStatus('loading');
    try {
      const rows = await listAllBanners();
      setItems(rows);
      setStatus('loaded');
      // Lazy per-banner stats in parallel. Non-fatal on individual failures.
      const results = await Promise.all(
        rows.map((b) =>
          getBannerStats(b.id)
            .then((s) => [b.id, s] as const)
            .catch(() => [b.id, null] as const),
        ),
      );
      const next: Record<string, BannerStats> = {};
      for (const [id, s] of results) if (s) next[id] = s;
      setStats(next);
    } catch (err) {
      setError(humanize(err));
      setStatus('error');
    }
  }

  useEffect(() => {
    if (!isAdmin) return;
    void reload();
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <div className="container mx-auto py-8 px-4">
        <p className="text-sm text-muted-foreground">Platform admin only.</p>
      </div>
    );
  }

  async function onPublish(id: string) {
    setBusy(id);
    setError(null);
    try {
      await publishBanner(id);
      await reload();
    } catch (err) {
      setError(humanize(err));
    } finally {
      setBusy(null);
    }
  }

  async function onArchive(id: string) {
    if (!window.confirm('Archive this banner? It will stop showing immediately.')) return;
    setBusy(id);
    setError(null);
    try {
      await archiveBanner(id);
      await reload();
    } catch (err) {
      setError(humanize(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight">Banners</h1>
            <p className="text-sm text-muted-foreground">
              Compose communication banners. Drafts are invisible until you publish.
            </p>
          </div>
          <Link
            to="/admin/banners/new"
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            + New banner
          </Link>
        </div>

        {error && (
          <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </p>
        )}
        {status === 'loading' && (
          <p className="text-sm text-muted-foreground">Loading…</p>
        )}
        {status === 'loaded' && items.length === 0 && (
          <div className="rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
            No banners yet. Click "+ New banner" to compose one.
          </div>
        )}
        {status === 'loaded' && items.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Title</th>
                  <th className="px-3 py-2 text-left">Severity</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Window</th>
                  <th className="px-3 py-2 text-left">Target</th>
                  <th className="px-3 py-2 text-right">Stats</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => {
                  const theme = BANNER_SEVERITY_THEMES[b.severity];
                  return (
                    <tr key={b.id} className="border-t border-border">
                      <td className="px-3 py-2">
                        <Link
                          to={`/admin/banners/${b.id}`}
                          className="font-medium hover:underline"
                        >
                          {b.title}
                        </Link>
                        {b.acknowledgement_required && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                            ack required
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex items-center rounded-full ${theme.bg} ${theme.text} px-2 py-0.5 text-xs`}
                        >
                          {theme.label}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <StatusPill status={b.status} />
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">
                        {formatDate(b.starts_at)}
                        {' → '}
                        {b.ends_at
                          ? formatDate(b.ends_at)
                          : '∞'}
                      </td>
                      <td className="px-3 py-2 text-xs text-muted-foreground">
                        {b.target_kind === 'all' && 'Everyone'}
                        {b.target_kind === 'specific_orgs' &&
                          `${b.target_org_ids.length} org(s)`}
                        {b.target_kind === 'specific_roles' &&
                          b.target_roles.join(', ')}
                        {b.target_tiers && b.target_tiers.length > 0 && (
                          <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">
                            tier: {b.target_tiers.join(', ')}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-xs whitespace-nowrap">
                        <StatsCell stats={stats[b.id]} status={b.status} />
                      </td>
                      <td className="px-3 py-2 text-right space-x-2">
                        {b.status === 'draft' && (
                          <button
                            type="button"
                            disabled={busy === b.id}
                            onClick={() => onPublish(b.id)}
                            className="rounded-md bg-green-600 px-2 py-1 text-xs text-white hover:opacity-90 disabled:opacity-50"
                          >
                            Publish
                          </button>
                        )}
                        {b.status !== 'archived' && (
                          <button
                            type="button"
                            disabled={busy === b.id}
                            onClick={() => onArchive(b.id)}
                            className="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-muted/40 disabled:opacity-50"
                          >
                            Archive
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatsCell({ stats, status }: { stats: BannerStats | undefined; status: BannerStatus }) {
  if (!stats) {
    return <span className="text-muted-foreground">—</span>;
  }
  if (stats.impressions === 0) {
    return (
      <span className="text-muted-foreground" title="No impressions yet">
        {status === 'draft' ? 'not live' : '0 impr'}
      </span>
    );
  }
  return (
    <div className="space-y-0.5">
      <div title="Impressions / unique viewers">
        <span className="font-medium">{stats.impressions.toLocaleString()}</span>
        <span className="text-muted-foreground"> impr / {stats.unique_viewers.toLocaleString()} uniq</span>
      </div>
      <div className="text-muted-foreground" title="Clicks / CTR">
        {stats.clicks.toLocaleString()} clk / {stats.ctr_pct.toFixed(1)}% CTR
      </div>
      {stats.acknowledgements > 0 && (
        <div className="text-muted-foreground" title="Acknowledgements / ack rate">
          {stats.acknowledgements.toLocaleString()} ack / {stats.ack_rate_pct.toFixed(1)}%
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }: { status: BannerStatus }) {
  const styles: Record<BannerStatus, string> = {
    draft: 'bg-gray-100 text-gray-700',
    published: 'bg-green-100 text-green-800',
    archived: 'bg-gray-100 text-gray-500',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[status]}`}
    >
      {status}
    </span>
  );
}
